import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ServiceCalendar } from "@/components/ServiceCalendar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, MapPin, Info, Users, Megaphone, Check, Loader2, CalendarDays } from "lucide-react";

export default function TeamSchedule() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyMine, setOnlyMine] = useState(false);
  const [fnFilter, setFnFilter] = useState("all");
  const [togglingId, setTogglingId] = useState(null);

  const load = async () => {
    try {
      const [s, a, ac] = await Promise.all([
        api.get("/services"),
        api.get("/planning/assignments"),
        api.get("/activities"),
      ]);
      setServices(s.data.filter((x) => (x.publish_state || (x.published ? "provisional" : "concept")) !== "concept"));
      setAssignments(a.data);
      setActivities(ac.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const forService = (id) => assignments.filter((a) => a.service_id === id);

  const functionOptions = useMemo(() => {
    const set = new Set();
    services.forEach((s) => (s.slots || []).forEach((sl) => set.add(sl.service_type_name)));
    return [...set].sort();
  }, [services]);

  const serviceItems = services.filter((s) => {
    if (onlyMine && !assignments.some((a) => a.service_id === s.id && a.user_id === user?.id)) return false;
    if (fnFilter !== "all" && !(s.slots || []).some((sl) => sl.service_type_name === fnFilter)) return false;
    return true;
  });

  const activityItems = (fnFilter !== "all" ? [] : activities)
    .filter((a) => !onlyMine || a.i_signed_up)
    .map((a) => ({ id: `act-${a.id}`, date: a.date, start_time: a.start_time || "", end_time: a.end_time || "", name: a.title, kind: "activity", activity: a }));

  const items = [...serviceItems, ...activityItems];

  const toggleSignup = async (a) => {
    setTogglingId(a.id);
    try {
      const { data } = a.i_signed_up ? await api.delete(`/activities/${a.id}/signup`) : await api.post(`/activities/${a.id}/signup`);
      setActivities((list) => list.map((x) => (x.id === a.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTogglingId(null); }
  };

  const isProvisional = (svc) => (svc.publish_state || (svc.published ? "provisional" : "concept")) === "provisional";
  const getStatus = (item) => item.kind === "activity"
    ? { tone: "sky", label: `Activiteit · ${item.activity.signup_count} aangemeld` }
    : { tone: isProvisional(item) ? "amber" : "red", label: `${forService(item.id).length} ingepland${isProvisional(item) ? " · voorlopig" : ""}` };

  const renderActivity = (a) => (
    <Card className="p-4 space-y-3" data-testid={`team-activity-${a.id}`}>
      <p className="font-heading font-bold text-lg flex items-center gap-2"><Megaphone className="w-5 h-5 text-sky-600" />{a.title}</p>
      <p className="text-xs text-muted-foreground capitalize flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{new Date(`${a.date}T12:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</p>
      {(a.start_time || a.end_time) && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Clock className="w-4 h-4" />{a.start_time || ""}{a.end_time ? `–${a.end_time}` : ""}</p>}
      {a.description && <p className="text-sm whitespace-pre-line">{a.description}</p>}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" />{a.signup_count} aangemeld:</span>
        {a.signups.length === 0 && <span className="text-xs text-muted-foreground">nog niemand</span>}
        {a.signups.map((s) => <span key={s.user_id} className="bg-slate-100 dark:bg-slate-800 text-xs px-2 py-0.5 rounded-full">{s.user_name}</span>)}
      </div>
      <Button variant={a.i_signed_up ? "outline" : "default"} className="w-full" disabled={togglingId === a.id} onClick={() => toggleSignup(a)} data-testid={`team-signup-toggle-${a.id}`}>
        {togglingId === a.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : a.i_signed_up ? <Check className="w-4 h-4 mr-2" /> : null}
        {a.i_signed_up ? "Aangemeld — afmelden" : "Aanmelden"}
      </Button>
    </Card>
  );

  const renderService = (svc) => {
    const people = forService(svc.id);
    const bySlot = {};
    svc.slots.forEach((sl) => (bySlot[sl.id] = { name: sl.service_type_name, people: [] }));
    people.forEach((a) => {
      if (!bySlot[a.slot_id]) bySlot[a.slot_id] = { name: a.service_type_name, people: [] };
      bySlot[a.slot_id].people.push(a.user_name);
    });
    return (
      <Card className="p-4" data-testid={`team-service-${svc.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium">{svc.name}</p>
          {isProvisional(svc)
            ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid={`team-provisional-${svc.id}`}>Voorlopig — kan nog wijzigen</span>
            : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300" data-testid={`team-final-${svc.id}`}>Definitief</span>}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <Clock className="w-3 h-3" /> {svc.start_time}–{svc.end_time}
          {svc.location && <><MapPin className="w-3 h-3 ml-1" /> {svc.location}</>}
        </p>
        {svc.notes && <p className="text-xs text-amber-700 mt-1 flex items-start gap-1" data-testid={`team-note-${svc.id}`}><Info className="w-3 h-3 mt-0.5 shrink-0" />{svc.notes}</p>}
        <div className="space-y-2 mt-3">
          {Object.entries(bySlot).map(([id, slot]) => (
            <div key={id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
              <p className="text-xs font-semibold mb-1">{slot.name}</p>
              <div className="flex flex-wrap gap-1.5">
                {slot.people.length === 0 && <span className="text-xs text-muted-foreground">Nog niemand ingepland</span>}
                {slot.people.map((name, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 text-xs px-2 py-1 rounded-full">
                    <Users className="w-3 h-3" /> {name}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {svc.slots.length === 0 && <p className="text-xs text-muted-foreground">Geen functies gedefinieerd.</p>}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="team-schedule-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Rooster</h1>
        <p className="text-muted-foreground mt-1">Voorlopige én definitieve diensten en activiteiten in één agenda. Voorlopige planning kan nog wijzigen.</p>
      </div>

      <div className="flex flex-wrap gap-4 text-xs font-medium text-muted-foreground" data-testid="team-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500" /> Definitieve dienst</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500" /> Voorlopige dienst</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sky-500" /> Activiteit / vergadering</span>
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="team-filters">
        <Button variant={onlyMine ? "default" : "outline"} size="sm" onClick={() => setOnlyMine((v) => !v)} data-testid="filter-mine">Alleen mijn diensten</Button>
        <Select value={fnFilter} onValueChange={setFnFilter}>
          <SelectTrigger className="w-56" data-testid="filter-function"><SelectValue placeholder="Alle functies" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle functies</SelectItem>
            {functionOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <ServiceCalendar
        services={items}
        calendarId="team"
        loading={loading}
        getStatus={getStatus}
        getNames={(item) => item.kind === "activity" ? (item.activity.signups || []).map((s) => s.user_name) : forService(item.id).map((a) => a.user_name)}
        renderDetails={(item) => item.kind === "activity" ? renderActivity(item.activity) : renderService(item)}
      />
    </div>
  );
}
